/**
 * Converters between the dataset wire format for NER entities
 * (`{ start_index, content, label, is_validated, ... }`) and the shared
 * `NerExampleEditor` format (`{ type, start, end, ... }` with exclusive end).
 *
 * Extra fields (e.g. `is_validated`, `confidence`) are carried through in both
 * directions so an edit round-trip never loses them — the span helpers in
 * `nerExampleSpans.js` preserve them via object spread.
 */

/**
 * Convert wire entities to editor entities.
 *
 * Defensive: entities whose span does not match the text (out of bounds,
 * empty, stale offsets where `content` no longer equals the text slice) or
 * that overlap an earlier accepted entity are dropped — the same invariant
 * the annotate UI has always enforced ("Cannot tag overlapping entities").
 */
export const toEditorEntities = (text, entities) => {
    const source = text || '';
    const sorted = [...(entities || [])]
        .filter(Boolean)
        .sort((left, right) => (left.start_index ?? 0) - (right.start_index ?? 0));
    const result = [];
    let lastEnd = 0;
    sorted.forEach((entity) => {
        const { start_index: start, content, label, ...rest } = entity;
        const length = (content || '').length;
        if (!Number.isInteger(start) || start < 0 || length === 0 || !label) return;
        const end = start + length;
        if (end > source.length) return;
        if (source.slice(start, end) !== content) return;
        if (result.length > 0 && start < lastEnd) return;
        result.push({ ...rest, type: label, start, end });
        lastEnd = end;
    });
    return result;
};

/**
 * Convert editor entities back to the wire format. `content` is re-derived
 * from the current text so edited offsets always stay consistent.
 *
 * Entities created or re-tagged in the editor carry no `is_validated` yet and
 * default to validated, matching the legacy tagger's behaviour when adding a
 * tag.
 */
export const fromEditorEntities = (text, entities) => {
    const source = text || '';
    return (entities || [])
        .filter(
            (entity) =>
                entity &&
                entity.type &&
                Number.isInteger(entity.start) &&
                Number.isInteger(entity.end) &&
                entity.start >= 0 &&
                entity.end <= source.length &&
                entity.start < entity.end
        )
        .sort((left, right) => left.start - right.start || left.end - right.end)
        .map(({ type, start, end, ...rest }) => ({
            ...rest,
            label: type,
            content: source.slice(start, end),
            start_index: start,
            is_validated: rest.is_validated ?? true,
        }));
};
